import { FC, useEffect, ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Popover } from 'antd';

import { userSelectors } from '@/redux/selectors';
import { userActions } from '@/redux/actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { AuthProviderEnum, PageDescription, SubmenuType } from '@/config';
import {
  ProfileInfo,
  LangSwitcher,
  SvgIcon,
  BalanceInfo,
  BrandLogo,
  TopNav,
} from '@/components';
import useDevice from '@/hooks/useDevice';

import amlbotLogo from '@/assets/images/logo-amlbot.svg';

import { HelpMenu } from './components/HelpMenu';
import { findCurrentPage } from './Header.helpers';
import styles from './Header.module.sass';

interface HeaderProps {
  isActiveTariffsPanel: boolean;
  children?: ReactNode;
  onToggleTariffsPanel: (isActive: boolean) => void;
}

const Header: FC<HeaderProps> = ({
  children,
  isActiveTariffsPanel,
  onToggleTariffsPanel,
}) => {
  const { data } = useAppSelector(userSelectors.getUserSlice);
  const balance = useAppSelector(userSelectors.getUserBalance);
  const isUserLocked = useAppSelector(userSelectors.checkIfUserLocked);
  const isAiTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);

  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isSidebarHidden = isAiTraceOnly && !isMobile;
  const [page, setPage] = useState<PageDescription | SubmenuType | null>(null);

  const stateBackPath = location.state?.backPath as string | undefined;
  const hasBackPath = !!(stateBackPath || page?.backPath);
  const isPromo = data?.promoBalance;

  useEffect(() => {
    const foundPage = findCurrentPage(location.pathname);
    setPage(foundPage);
  }, [location, t]);

  const backHandler = () => {
    const backPath = stateBackPath || page?.backPath;
    if (!backPath) return;

    if (stateBackPath) {
      navigate(stateBackPath);
    } else {
      navigate(backPath, {
        state: {
          ...(!!location?.state?.queryHash
            ? { queryHash: location?.state?.queryHash }
            : {}),
          filter: {
            ...(!!location?.state?.filter ? location?.state?.filter : {}),
          },
        },
      });
    }
  };

  const signoutHandler = () => {
    dispatch(userActions.signout());
  };

  const prepareUserName = () => {
    if (
      data?.provider === AuthProviderEnum.GOOGLE ||
      data?.provider === AuthProviderEnum.DEFAULT
    ) {
      return data?.fullName || data.emailAddress;
    }
    return data?.fullName || 'user';
  };

  const classes = cx(styles.header, {
    [styles['has-back']]: hasBackPath,
    [styles['is-full-width']]: isSidebarHidden,
  });

  return (
    <header className={classes}>
      {isSidebarHidden && (
        <>
          <BrandLogo className={styles.brand} />
          <TopNav />
        </>
      )}

      {!isSidebarHidden && isMobile && (
        <Link to="/" className={styles.logo}>
          <img src={amlbotLogo} alt="AMLBot" />
        </Link>
      )}

      {page && hasBackPath && !isSidebarHidden && (
        <button
          type="button"
          className={cx(styles.headline, styles['is-clickable'])}
          onClick={backHandler}
        >
          <span className={styles.back}>
            <SvgIcon name="arrow-back" />
          </span>
          <span className={styles.title}>{t(page.i18NameKey || '')}</span>
        </button>
      )}

      {page && !hasBackPath && !isSidebarHidden && (
        <div className={styles.headline}>
          <span className={styles.title}>{t(page.i18NameKey || '')}</span>
        </div>
      )}
      <div className={styles.actions}>
        <BalanceInfo
          isActive={isActiveTariffsPanel}
          balance={balance}
          isUserLocked={isUserLocked}
          isPromo={isPromo}
          onToggle={onToggleTariffsPanel}
        />

        <LangSwitcher isShowArrow={false} isRounded />

        <HelpMenu />

        <ProfileInfo name={prepareUserName()} />

        {isSidebarHidden && (
          <Popover
            zIndex={9999}
            arrow={false}
            content={t('translation.sign_out')}
            open={isMobile ? false : undefined}
          >
            <button
              type="button"
              className={styles.logout}
              onClick={signoutHandler}
              aria-label={t('translation.sign_out')}
            >
              <SvgIcon name="logout" />
            </button>
          </Popover>
        )}
      </div>

      {children}
    </header>
  );
};

export default Header;
